import type { DataScope, MembershipArea } from '@crm/permissions';
import { api } from '@web/core/http/api';
import type { BrandColor } from '@web/core/theme/brand';

/** Mirrors of the API session DTOs (apps/api/src/modules/auth/infra/http). */
export interface SessionUser {
  id: string;
  name: string;
  /** Optional for members (spec 016). */
  email: string | null;
  /** The sign-in: `name@organization`. */
  username: string;
  avatarUrl: string | null;
  mustChangePassword: boolean;
}

/**
 * What the organization uses the CRM for (spec 044): shapes the menu, the home blocks, the
 * default report tab and a few labels; the API never changes with it. Mirrors
 * `apps/api/src/modules/organizations/domain/usage-profile.ts`.
 */
export const USAGE_PROFILES = ['sales', 'service', 'both'] as const;

export type UsageProfile = (typeof USAGE_PROFILES)[number];

export interface SessionOrganization {
  id: string;
  name: string;
  slug: string;
  usageProfile: UsageProfile;
}

export interface SessionMembership {
  id: string;
  role: string;
  /** Where the member works (spec 044): `service` has no sales permissions. */
  area: MembershipArea;
  status: 'active' | 'inactive';
}

/**
 * What this person's screens show: the organization's profile narrowed by the member's area
 * (spec 044). A member in `service` of an organization that does `both` sees no sales; one in
 * `sales` of an organization that only does `service` follows the organization.
 */
export function effectiveUsageProfile(
  organization: UsageProfile,
  area: MembershipArea,
): UsageProfile {
  if (organization === 'both') return area;
  return organization;
}

/** The organization's demo period (on a demo plan). */
export interface SessionDemo {
  endsAt: string;
  /** Ended: the API refuses everything but `GET /auth/me` (`DEMO_EXPIRED`). */
  expired: boolean;
}

export interface Me {
  user: SessionUser;
  organization: SessionOrganization;
  membership: SessionMembership & {
    permissions: string[];
    dataScope: DataScope;
    unitIds: string[];
    defaultUnitId: string | null;
    teamIds: string[];
    /** The web's primary color: the member's default unit's (else the organization's). */
    brandColor: BrandColor;
  };
  /** `null` = not on a demo plan. */
  demo: SessionDemo | null;
}

export interface AuthenticatedSession {
  status: 'authenticated';
  user: SessionUser;
  organization: SessionOrganization;
  membership: SessionMembership;
}

export interface OrganizationChoice {
  id: string;
  name: string;
  slug: string;
  role: string;
}

export type LoginResult =
  | AuthenticatedSession
  | { status: 'organization_selection_required'; organizations: OrganizationChoice[] };

export type RegistrationMode = 'open' | 'invite' | 'closed';

export interface RegisterInput {
  organizationName: string;
  name: string;
  email: string;
  password: string;
  inviteCode?: string;
}

/** Calls of the session flow. The tokens stay in httpOnly cookies set by the API. */
export const authApi = {
  /** `login`: the username (`bruno@aplopes`) or the e-mail. */
  login: (input: { login: string; password: string }) =>
    api.post<LoginResult>('/auth/login', input),
  selectOrganization: (organizationId: string) =>
    api.post<AuthenticatedSession>('/auth/select-organization', { organizationId }),
  logout: () => api.post<{ status: 'signed_out' }>('/auth/logout'),
  me: (signal?: AbortSignal) => api.get<Me>('/auth/me', signal ? { signal } : {}),
  registrationMode: () => api.get<{ mode: RegistrationMode }>('/auth/register/mode'),
  register: (input: RegisterInput) => api.post<AuthenticatedSession>('/auth/register', input),
  forgotPassword: (email: string) =>
    api.post<{ status: 'accepted' }>('/auth/forgot-password', { email }),
  resetPassword: (input: { token: string; password: string }) =>
    api.post<{ status: 'password_reset' }>('/auth/reset-password', input),
  changePassword: (input: { currentPassword?: string; newPassword: string }) =>
    api.patch<{ status: 'password_changed' }>('/users/me/password', input),
};
